/**
 * Notification creation helpers for data_notifications.
 * - Unrecognized expense: bank transactions with no category or needs_review.
 * - Deduplication: one open notification per (type, related_entity_id).
 */

import { supabase } from '@/integrations/supabase/client';
import type { DataNotificationInsert } from '@/integrations/supabase/payrollNotificationsTypes';

/** Confidence below this → treat as unrecognized (needs review). Default 0.8. */
export const UNRECOGNIZED_CONFIDENCE_THRESHOLD = 0.8;

/**
 * Criteria for "unrecognized expense":
 * - Option A: category_type IS NULL (never classified)
 * - Option B: needs_review = true OR classification_confidence < UNRECOGNIZED_CONFIDENCE_THRESHOLD
 */
export function isUnrecognizedExpense(tx: {
  category_type: string | null;
  needs_review?: boolean | null;
  classification_confidence?: number | null;
}): boolean {
  if (tx.category_type == null) return true;
  if (tx.needs_review === true) return true;
  const conf = tx.classification_confidence;
  if (conf != null && conf < UNRECOGNIZED_CONFIDENCE_THRESHOLD) return true;
  return false;
}

export interface UnrecognizedExpenseSnippet {
  merchant?: string | null;
  amount: number;
  date: string;
  source?: string | null;
}

/**
 * Ensures one open unrecognized_expense notification exists for the given
 * bank transaction. Idempotent: does not duplicate.
 */
export async function createUnrecognizedExpenseNotificationIfNeeded(
  companyId: string,
  transactionId: string,
  dataSnippet: UnrecognizedExpenseSnippet
): Promise<void> {
  const row: DataNotificationInsert = {
    company_id: companyId,
    type: 'unrecognized_expense',
    severity: Math.abs(dataSnippet.amount) >= 5000 ? 'critical' : 'this_week',
    source: 'bank',
    related_entity_type: 'bank_transaction',
    related_entity_id: transactionId,
    title: 'Identify this expense',
    description: 'We use this to predict your monthly bills and expense breakdown.',
    data_snippet: dataSnippet as Record<string, unknown>,
    status: 'open',
  };

  const { error } = await (supabase as any).from('data_notifications').insert(row);
  if (error && error.code !== '23505') {
    throw error;
  }
}

// ---------------------------------------------------------------------------
// POS / payout mismatch
// ---------------------------------------------------------------------------

export interface POSMismatchSnippet {
  platform?: string;
  payout_amount?: number;
  payout_date?: string;
  location?: string;
  bank_amount?: number;
  bank_date?: string;
  bank_description?: string;
}

/**
 * Ensures one open pos_mismatch notification exists for the given entity.
 * Idempotent: does not duplicate.
 */
export async function createPOSMismatchNotificationIfNeeded(
  companyId: string,
  related_entity_type: 'sales_transaction' | 'bank_transaction',
  related_entity_id: string,
  dataSnippet: POSMismatchSnippet
): Promise<void> {
  const title =
    related_entity_type === 'sales_transaction'
      ? 'POS payout has no matching bank deposit'
      : 'Bank deposit has no matching POS payout';
  const description = 'This affects sales accuracy and cash timing.';

  const row: DataNotificationInsert = {
    company_id: companyId,
    type: 'pos_mismatch',
    severity: 'this_week',
    source: related_entity_type === 'sales_transaction' ? 'pos' : 'bank',
    related_entity_type,
    related_entity_id,
    title,
    description,
    data_snippet: dataSnippet as Record<string, unknown>,
    status: 'open',
  };

  const { error } = await (supabase as any).from('data_notifications').insert(row);
  if (error && error.code !== '23505') {
    throw error;
  }
}
